import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Search, TrendingUp, ClipboardCheck, Mic, ChevronRight, Users } from "lucide-react";

export default function ClinicianDashboard() {
  const [patients, setPatients] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/clinician/patients").then((r) => setPatients(r.data.patients));
  }, []);

  if (!patients) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>;

  const filtered = patients.filter(
    (p) => p.name.toLowerCase().includes(q.toLowerCase()) ||
      (p.manogene_id || "").toLowerCase().includes(q.toLowerCase()) ||
      p.email.toLowerCase().includes(q.toLowerCase())
  );

  const totals = {
    review: patients.reduce((a, p) => a + p.pending_review, 0),
    esc: patients.reduce((a, p) => a + p.escalations, 0),
  };

  return (
    <div>
      <div className="eyebrow">Clinician workspace</div>
      <h1 className="font-head text-3xl font-bold text-ink mt-1">Patient review</h1>
      <p className="text-ink-soft mt-1">Voice triage and genetic reports awaiting your interpretation.</p>

      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: "Patients", value: patients.length, icon: Users, color: "#1E3A8A" },
          { label: "Pending review", value: totals.review, icon: ClipboardCheck, color: "#B45309" },
          { label: "Escalations", value: totals.esc, icon: TrendingUp, color: "#B91C1C" },
          { label: "Voice sessions", value: patients.reduce((a, p) => a + p.sessions, 0), icon: Mic, color: "#3B82F6" },
        ].map((s) => (
          <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-4">
            <s.icon className="h-5 w-5 mb-2" style={{ color: s.color }} />
            <div className="mono text-2xl font-semibold text-ink">{s.value}</div>
            <div className="eyebrow mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 relative max-w-sm">
        <Search className="h-4 w-4 text-ink-muted absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, MANOGENE ID, email"
          data-testid="patient-search"
          className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2.5 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/15" />
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-ink-muted">
              <th className="px-4 py-3 font-semibold">Patient</th>
              <th className="px-4 py-3 font-semibold">MANOGENE ID</th>
              <th className="px-4 py-3 font-semibold text-center">Sessions</th>
              <th className="px-4 py-3 font-semibold text-center">Active tests</th>
              <th className="px-4 py-3 font-semibold text-center">Review</th>
              <th className="px-4 py-3 font-semibold text-center">Escalations</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map((p) => (
              <tr key={p.id} className="hover:bg-slate-50 transition-colors" data-testid={`patient-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-ink">{p.name}</div>
                  <div className="text-xs text-ink-muted">{p.email}</div>
                </td>
                <td className="px-4 py-3 mono text-brand">{p.manogene_id}</td>
                <td className="px-4 py-3 text-center">{p.sessions}</td>
                <td className="px-4 py-3 text-center">{p.active_tests}</td>
                <td className="px-4 py-3 text-center">
                  {p.pending_review > 0 ? <span className="inline-flex rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">{p.pending_review}</span> : <span className="text-ink-muted">—</span>}
                </td>
                <td className="px-4 py-3 text-center">
                  {p.escalations > 0 ? <span className="inline-flex rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-xs font-semibold">{p.escalations}</span> : <span className="text-ink-muted">—</span>}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/clinician/patient/${p.id}`} data-testid={`open-patient-${p.id}`}
                    className="inline-flex items-center gap-1 text-brand font-semibold hover:underline">
                    Open <ChevronRight className="h-4 w-4" />
                  </Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-ink-muted">No patients found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
